import { Card, Button, Avatar, Tag } from "antd"
import { EditOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { RoutingType, CrewMember } from "../routingApi"

interface RouteViewSettingsProps {
  route: any
  state: any
  crew?: CrewMember[]
  setIsUpdateRouteModalOpen: (open: boolean) => void
}

const RouteViewSettings = ({ route, state, crew, setIsUpdateRouteModalOpen }: RouteViewSettingsProps) => {

  return (
    <div style={{ flex: 1, padding: '24px', backgroundColor: '#f5f5f5' }}>
      <Card
        style={{ maxWidth: 800, margin: '40px auto 0 auto' }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px', marginTop: '10px' }}>General Settings</div>
              <div style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: 400 }}>Manage the general settings for the route.</div>
            </div>
            <Button icon={<EditOutlined />} type="text" onClick={() => setIsUpdateRouteModalOpen(true)}>
              Manage
            </Button>
          </div>
        }
        bordered={false}
      >
        <div style={{ padding: '16px 0' }}>
          {/* Route Name */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>Route Name</div>
            <div style={{ fontSize: '16px', fontWeight: 500, color: '#000' }}>
              {state.route.name || 'Mowing Route'}
            </div>
          </div>

          {/* Route Type */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>Route Type</div>
            <div style={{ fontSize: '16px', fontWeight: 500, color: '#000' }}>
              {route.routeType === RoutingType.Planned ? "Planned" : "On Demand"}
            </div>
          </div>

          {/* Planned Weekday (only show if route type is Planned) */}
          {route?.routeType === RoutingType.Planned && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>Planned Weekday</div>
              <div style={{ fontSize: '16px', fontWeight: 500, color: '#000' }}>
                {(state.route.defaultScheduleConfig?.dayOfWeek || '').charAt(0).toUpperCase() + (state.route.defaultScheduleConfig?.dayOfWeek || '').slice(1).toLowerCase()}
              </div>
            </div>
          )}

          {/* Default Schedule (only show if route type is Planned) */}
          {route?.routeType === RoutingType.Planned && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>Default Schedule</div>
              <div style={{ fontSize: '16px', color: '#000' }}>
                <div>
                  <span style={{ fontWeight: 500, color: '#8c8c8c' }}>Starts</span> <span style={{ fontWeight: 600 }}>
                    {state.route.defaultScheduleConfig?.startDate && dayjs(state.route.defaultScheduleConfig.startDate, 'YYYY-MM-DD').isValid()
                      ? dayjs(state.route.defaultScheduleConfig.startDate, 'YYYY-MM-DD').format('MMM D, YYYY')
                      : ''}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: 500, color: '#8c8c8c' }}>Repeats</span> <span style={{ fontWeight: 500 }}>
                    {state.route.defaultScheduleConfig?.repeatUnits === 'month'
                      ? `Monthly on the ${(() => {
                        const repeatPlace = state.route.defaultScheduleConfig?.repeatPlace || ''
                        const parts = repeatPlace.split(',').map((part: string) => part.trim())
                        const orderedParts = ['1st', '2nd', '3rd', '4th', 'Last']
                        const sortedParts = orderedParts.filter(order =>
                          parts.some((part: string) => part.toLowerCase().includes(order.toLowerCase()))
                        )
                        return sortedParts.length > 0 ? sortedParts.join(', ') : repeatPlace
                      })()} ${(state.route.defaultScheduleConfig?.dayOfWeek || '').charAt(0).toUpperCase() + (state.route.defaultScheduleConfig?.dayOfWeek || '').slice(1).toLowerCase()}`
                      : (state.route.defaultScheduleConfig?.repeatUnits as string) === 'quarter'
                        ? `Quarterly on the ${(() => {
                          const repeatPlace = state.route.defaultScheduleConfig?.repeatPlace || ''
                          const parts = repeatPlace.split(',').map((part: string) => part.trim())
                          const orderedParts = ['1st', '2nd', '3rd', '4th', 'Last']
                          const sortedParts = orderedParts.filter(order =>
                            parts.some((part: string) => part.toLowerCase().includes(order.toLowerCase()))
                          )
                          return sortedParts.length > 0 ? sortedParts.join(', ') : repeatPlace
                        })()} ${(state.route.defaultScheduleConfig?.dayOfWeek || '').charAt(0).toUpperCase() + (state.route.defaultScheduleConfig?.dayOfWeek || '').slice(1).toLowerCase()} of the ${(state.route.defaultScheduleConfig?.repeatQuarter || '').replace(/,/g, ', ')} quarter`
                        : state.route.defaultScheduleConfig?.repeatUnits === 'year'
                          ? `Annually on the ${(() => {
                            const repeatPlace = state.route.defaultScheduleConfig?.repeatPlace || ''
                            const parts = repeatPlace.split(',').map((part: string) => part.trim())
                            const orderedParts = ['1st', '2nd', '3rd', '4th', 'Last']
                            const sortedParts = orderedParts.filter(order =>
                              parts.some((part: string) => part.toLowerCase().includes(order.toLowerCase()))
                            )
                            return sortedParts.length > 0 ? sortedParts.join(', ') : repeatPlace
                          })()} ${(state.route.defaultScheduleConfig?.dayOfWeek || '').charAt(0).toUpperCase() + (state.route.defaultScheduleConfig?.dayOfWeek || '').slice(1).toLowerCase()} of ${(() => {
                            const repeatMonth = state.route.defaultScheduleConfig?.repeatMonth || ''
                            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
                            const sortedMonthNumbers = repeatMonth.split(',').map((num: string) => parseInt(num.trim())).filter((num: number) => !isNaN(num)).sort((a: number, b: number) => a - b)
                            const monthNamesList = sortedMonthNumbers.map((num: number) => monthNames[num - 1]).filter(Boolean)
                            return monthNamesList.join(', ')
                          })()}`
                          : `Every ${state.route.defaultScheduleConfig?.repeatCount || 1} Week`}
                  </span>
                </div>
                {state.route.defaultScheduleConfig?.endType === 'on' && (
                  <div>
                    <span style={{ fontWeight: 500, color: '#8c8c8c' }}>Ends</span> <span style={{ fontWeight: 600 }}>
                      {state.route.defaultScheduleConfig?.endDate && dayjs(state.route.defaultScheduleConfig.endDate, 'YYYY-MM-DD').isValid()
                        ? dayjs(state.route.defaultScheduleConfig.endDate, 'YYYY-MM-DD').format('MMM D, YYYY')
                        : ''}
                    </span>
                  </div>
                )}
                {state.route.defaultScheduleConfig?.endType === 'after' && (
                  <div>
                    <span style={{ fontWeight: 600 }}>After {state.route.defaultScheduleConfig?.occurrences || state.route.defaultScheduleConfig?.repeatCount || 1} times</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Work Day Settings */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>Work Day Settings</div>
            <div style={{ fontSize: '16px', color: '#000' }}>
              <span style={{ fontWeight: 600 }}>
                {state.route.dayStartTime && dayjs(state.route.dayStartTime).isValid() && !state.route.dayStartTime.startsWith('0001-01-01')
                  ? dayjs(state.route.dayStartTime).format('h:mm A')
                  : '8:00 AM'} - {state.route.dayEndTime && dayjs(state.route.dayEndTime).isValid() && !state.route.dayEndTime.startsWith('0001-01-01')
                    ? dayjs(state.route.dayEndTime).format('h:mm A')
                    : '4:00 PM'}
              </span>
              {state.route.hasBreakTime && (
                <>
                  {' | '}
                  <span style={{ color: '#8c8c8c' }}>Estimated Break Time</span> <span style={{ fontWeight: 600 }}>
                    {Math.floor(state.route.estimatedBreakTimeMinutes / 60)}:{String(state.route.estimatedBreakTimeMinutes % 60).padStart(2, '0')}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Crew Members */}
          <div>
            <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '12px' }}>Crew Members</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {state.route.crewMemberIds ? (
                state.route.crewMemberIds.split(',')
                  .map((id: string) => crew?.find((member: CrewMember) => member.id === parseInt(id)))
                  .filter(Boolean)
                  .map((member: CrewMember) => (
                    <div key={member!.id} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Avatar src={member!.avatar} size={32}>
                        {member!.name.charAt(0)}
                      </Avatar>
                      <span style={{ fontSize: '16px', fontWeight: 500, color: '#000' }}>
                        {member!.name}
                      </span>
                      {member!.id === state.route.crewLeadId && (
                        <Tag color="gold" style={{ marginLeft: 'auto' }}>Crew Lead</Tag>
                      )}
                    </div>
                  ))
              ) : (
                <div style={{ color: '#8c8c8c', fontSize: '16px' }}>No crew members assigned</div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default RouteViewSettings